import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
// Removed unused imports
import { ErrorType } from "@/types";
import { Product, ProductStatus, ProductTypeEnum } from "@/types/system-setting/product";
import ProductSection from "./product-details";
import { useAddProductMutation, useUpdateProductMutation } from "@/services/productApi";
import ProductPriceSection from "./price-details";
import { deleteCognitoImage, UploadCognitoImage } from "@/lib/utils/s3Service";
const { VITE_VIEW_S3_URL } = import.meta.env;

export const initialProduct: Product = {
  sku: "",
  product_name: "",
  vendor: "",
  description: "",
  flexible_price: false,
  price: 0,
  cost: 0,
  type: ProductTypeEnum.other_product,
  manage_inventory: false,
  quantity: 0,
  income_category_id: "",
  sale_tax_id: 0,
  status: ProductStatus.ACTIVE,
  image_url: ""
};

interface ProductFormInterface {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: string;
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: any;
  editModeData?: Product;
}

const AddProductForm = ({
  isOpen,
  setOpen,
  action,
  editModeData,
  setAction,
  refetch,
}: ProductFormInterface) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { toast } = useToast();
  const [selectedImageFiles, setSelectedImageFiles] = useState<File[] | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const form = useForm<Product>({
    mode: "all",
    defaultValues: initialProduct,
  });
  const [
    addProduct,
    {
      isLoading: addProductLoading,
      error: productAddError,
      isError: productAddErrorBool,
    },
  ] = useAddProductMutation();
  const [
    updateProduct,
    {
      isLoading: updateProductLoading,
      error: productUpdateError,
      isError: productUpdateErrorBool,
    },
  ] = useUpdateProductMutation();
  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    getValues,
    formState: { isSubmitting, errors },
  } = form;

  const watcher = watch();

  useEffect(() => {
    if (action == "edit") {
    } else if (action == "add") {
      // need to add initial value here
      reset(initialProduct, {
        keepIsSubmitted: false,
        keepSubmitCount: false,
        keepDirtyValues: false,
        keepDefaultValues: true,
      });
    }
  }, [action, reset]);

  const handleClose = () => {
    clearErrors();
    setAction("add");
    // Clean up preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setSelectedImageFiles(null);
    setPreviewUrls([]);
    reset(initialProduct, {
      keepIsSubmitted: false,
      keepSubmitCount: false,
      keepDirtyValues: false,
      keepDefaultValues: true,
    });
    setOpen(false);
  };
  useEffect(() => {
    if (action == "edit" && editModeData) {
      console.log({ editModeData }, "edit");
      reset(editModeData as Product);
    } else if (action == "add" && editModeData == undefined) {
      console.log({ initialProduct }, "add");
      reset(initialProduct, {
        keepIsSubmitted: false,
        keepSubmitCount: false,
      });
    }
  }, [action, editModeData, reset]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const onSubmit = async (data: Product) => {
    let payload: Product;
    const product_name = data.product_name.toLowerCase();

    if (orgId) {
      payload = {
        ...data,
        product_name: data.product_name.toLowerCase(),
        description:
          data?.description?.trim() === ""
            ? ""
            : data?.description?.toLowerCase(),
      income_category_id:data.income_category_id.toString(),
      price:data.flexible_price ? 0:data.price,
      cost :data.type===ProductTypeEnum.wallet ? 0 : data.cost
      };
      console.log("payload",data)

       if (selectedImageFiles && selectedImageFiles.length > 0) {
            try {
              const selectedImage = selectedImageFiles[0];
              if (data.image_url !== "" && data.image_url) {
                await deleteCognitoImage(data.image_url as string);
              }
              const getUrl = await UploadCognitoImage(selectedImage);
              payload = {
                ...payload,
                image_url: getUrl?.location as string,
              };
            } catch (error) {
              console.error("Upload failed:", error);
              toast({
                variant: "destructive",
                title: "Image Upload Failed",
                description: "Please try again.",
              });
              return;
            }
          }
      try {
        if (action === "add") {
          const resp = await addProduct(payload).unwrap();
          if (resp) {
            toast({
              variant: "success",
              title: "Product saved successfully",
            });
            refetch();
            handleClose();
          }
        } else if (action === "edit") {
          const resp = await updateProduct({
            ...payload,
            id:payload.id
          }).unwrap();
          if (resp) {
            toast({
              variant: "success",
              title: "Product updated successfully",
            });
            refetch();
            handleClose();
          }
        }
      } catch (error) {
        console.error("Error", { error });
        if (error && typeof error === "object" && "data" in error) {
          const typedError = error as ErrorType;
          toast({
            variant: "destructive",
            title: "Error in Submission",
            description: typedError.data?.detail ?? typedError.data?.message,
          });
        } else {
          toast({
            variant: "destructive",
            title: "Error in Submission",
            description: `${error}||Something Went Wrong.`,
          });
        }
      }
    }
  };

  const handleFileUpload = (files: File[] | null) => {
    // Clean up previous preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    
    if (files && files.length > 0) {
      setSelectedImageFiles(files);
      // Create preview URLs for new files
      const urls = files.map(file => URL.createObjectURL(file));
      setPreviewUrls(urls);
    } else {
      setSelectedImageFiles(null);
      setPreviewUrls([]);
    }
  };

  const handleRemoveImage = async () => {
    // Clean up preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    
    const currentImageUrl = watcher.image_url;
    if (currentImageUrl) {
      try {
        await deleteCognitoImage(currentImageUrl as string);
      } catch (error) {
        console.error("Error deleting image from S3:", error);
      }
    }
    setSelectedImageFiles(null);
    setPreviewUrls([]);
    setValue("image_url", "");
  };


  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="lg:!max-w-[1100px] py-0 p-0 custom-scrollbar h-full bg-[#F8F9FA] w-[85%] sm:w-full sm:max-w-3xl"
      >
        <FormProvider {...form}>
          <form
            noValidate
            className="pb-4 w-full"
            onSubmit={handleSubmit(onSubmit, onError)} // need to add on submit here
            key={action}
          >
            <SheetHeader className="sticky top-0 z-40 pt-4 px-6 bg-white border-b shadow-md w-full pb-2">
              <SheetTitle >
                <div className="flex justify-between gap-5 items-center  bg-white">
                  <div className="text-lg font-semibold flex flex-col justify-center items-start">
                    <div className="w-[20px]"></div>
                    <span className="text-black pr-1 font-semibold">
                      Add Product
                    </span>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        Dashboard
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {editModeData ? "Remove" : "Add"} Product
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center gap-4">
                    <Button
                      type="button"
                      className="w-[100px] text-center flex items-center gap-2 border-primary"
                      variant={"outline"}
                      onClick={handleClose}
                    >
                      <i className="fa fa-xmark "></i>
                      Cancel
                    </Button>

                    <LoadingButton
                      type="submit"
                      className="w-[100px] bg-primary text-black text-center flex items-center gap-2"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {!isSubmitting && (
                        <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                      )}
                      {editModeData ? "Update" : "Save"}
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <div className="h-[100%] mt-4 flex flex-col items-center gap-4 overflow-y-auto  pb-6">
              <ProductSection 
                control={control} 
                errors={errors} 
                watch={watch}
                selectedImageFiles={selectedImageFiles}
                previewUrls={previewUrls}
                isUploadingImage={isUploadingImage}
                onFileUpload={handleFileUpload}
                onRemoveImage={handleRemoveImage}
                VITE_VIEW_S3_URL={VITE_VIEW_S3_URL}
              />
              <div className="w-[95%]">
                <ProductPriceSection
                  control={control}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  getValues={getValues}
                  register={register}
                />
              </div>
              {/* <HardwareCheckInControlSection
                control={control}
                errors={errors}
                watch={watch}
              /> */}
            </div>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default AddProductForm;
